import { useCurrentOrganization } from '@/contexts/OrganizationContext';

/**
 * Codici dei piani SaaS.
 *  - trial:         prova 7 giorni gratuita (= studio illimitato fino a scadenza)
 *  - trial_expired: trial scaduto → tutto bloccato (derivato da plan='trial' AND trial_ends_at < now)
 *  - solo:          €19/mese (1 utente, limiti 15/15/15)
 *  - studio:        €49/mese (3 utenti, illimitato salvo team mgmt e API)
 *  - team:          €149/mese (10 utenti, multi-org, API)
 *  - past_due:      pagamento fallito → tutto bloccato
 *  - cancelled:     subscription cancellata → tutto bloccato
 *
 * La matrice completa è in `docs/PRICING.md` (single source of truth UI + marketing).
 */
export type PlanCode = 'trial' | 'trial_expired' | 'solo' | 'studio' | 'team' | 'past_due' | 'cancelled';

export interface PlanInfo {
  code: PlanCode;
  /** Etichetta italiana per UI. */
  label: string;
  /** Variante del Badge shadcn. */
  variant: 'default' | 'secondary' | 'outline' | 'destructive';
}

const PLAN_META: Record<string, PlanInfo> = {
  trial:         { code: 'trial',         label: 'Trial',          variant: 'secondary' },
  trial_expired: { code: 'trial_expired', label: 'Trial scaduto',  variant: 'destructive' },
  solo:          { code: 'solo',          label: 'Solo',           variant: 'outline' },
  studio:        { code: 'studio',        label: 'Studio',         variant: 'default' },
  team:          { code: 'team',          label: 'Team',           variant: 'default' },
  past_due:      { code: 'past_due',      label: 'Sospeso',        variant: 'destructive' },
  cancelled:     { code: 'cancelled',     label: 'Cancellato',     variant: 'destructive' },
};

/**
 * Ritorna il piano corrente dell'organization attiva.
 *
 * Sorgente attuale: `organizations.plan`.
 * Post-Lemon Squeezy (F33): la sorgente di verità è `subscriptions.plan_code`,
 * ma il webhook aggiorna anche `organizations.plan` per compat.
 *
 * Derivazione `trial_expired`: se `plan='trial'` MA `trial_ends_at < NOW()`
 * mappiamo a 'trial_expired' (vero gate è DB via org_can_write — questo serve
 * solo a far apparire il banner "trial scaduto" lato UI).
 */
export function useOrgPlan(): PlanInfo {
  const { currentOrganization } = useCurrentOrganization();
  const planCode = String(currentOrganization?.plan ?? 'trial').toLowerCase();
  if (planCode === 'trial' && currentOrganization?.trial_ends_at) {
    const trialEnd = new Date(currentOrganization.trial_ends_at).getTime();
    if (Number.isFinite(trialEnd) && trialEnd < Date.now()) {
      return PLAN_META.trial_expired;
    }
  }
  return PLAN_META[planCode] ?? PLAN_META.trial;
}

/** Chiave delle feature flag boolean (presenza/assenza). */
export type FeatureFlag =
  | 'members'         // pagina gestione membri team (multi-utente)
  | 'view3d'          // vista 3D fotorealistica (V2.4 Three.js)
  | 'pdfBranding'     // logo personalizzato dell'org nei PDF
  | 'technicalSheet'  // export Capitolato Tecnico PDF (normative)
  | 'excelExport'     // export preventivo in formato Excel
  | 'mapDrawing'      // drawing tools + KPI di zona sulla mappa clienti
  | 'apiAccess';      // accesso API REST (futuro)

/** Chiave dei limiti numerici (-1 = illimitato). */
export type LimitKey =
  | 'maxMembers'
  | 'maxCustomers'
  | 'maxEstimates'
  | 'maxStratigraphies';

export interface PlanRules extends Record<FeatureFlag, boolean>, Record<LimitKey, number> {}

/** Costante: -1 nelle limit key indica "illimitato". */
export const UNLIMITED = -1;

/**
 * Matrice ufficiale dei piani. Single source of truth per UI gate, upsell, e
 * marketing (homepage prezzi). Mantenere allineata a `docs/plan-matrix.md`.
 *
 * Limiti numerici: numero intero = tetto totale (non /mese); UNLIMITED = -1.
 * Per Solo: 15 clienti / 15 preventivi / 15 stratigrafie custom (totali, non
 * mensili — la leva di upgrade è raggiungere il tetto, non un reset periodico).
 */
export const PLAN_RULES: Record<PlanCode, PlanRules> = {
  trial: {
    members: true, view3d: true, pdfBranding: true, technicalSheet: true,
    excelExport: true, mapDrawing: true, apiAccess: false,
    maxMembers: 3, maxCustomers: UNLIMITED, maxEstimates: UNLIMITED, maxStratigraphies: UNLIMITED,
  },
  solo: {
    members: false, view3d: false, pdfBranding: false, technicalSheet: false,
    excelExport: false, mapDrawing: false, apiAccess: false,
    maxMembers: 1, maxCustomers: 15, maxEstimates: 15, maxStratigraphies: 15,
  },
  studio: {
    // Studio = single-user professionista premium. Gestione team multi-utente
    // (inviti, ruoli, audit) è esclusiva Team — vedi docs/PRICING.md.
    members: false, view3d: true, pdfBranding: true, technicalSheet: true,
    excelExport: true, mapDrawing: true, apiAccess: false,
    maxMembers: 1, maxCustomers: UNLIMITED, maxEstimates: UNLIMITED, maxStratigraphies: UNLIMITED,
  },
  team: {
    members: true, view3d: true, pdfBranding: true, technicalSheet: true,
    excelExport: true, mapDrawing: true, apiAccess: true,
    maxMembers: 10, maxCustomers: UNLIMITED, maxEstimates: UNLIMITED, maxStratigraphies: UNLIMITED,
  },
  past_due: {
    members: false, view3d: false, pdfBranding: false, technicalSheet: false,
    excelExport: false, mapDrawing: false, apiAccess: false,
    maxMembers: 0, maxCustomers: 0, maxEstimates: 0, maxStratigraphies: 0,
  },
  cancelled: {
    members: false, view3d: false, pdfBranding: false, technicalSheet: false,
    excelExport: false, mapDrawing: false, apiAccess: false,
    maxMembers: 0, maxCustomers: 0, maxEstimates: 0, maxStratigraphies: 0,
  },
  trial_expired: {
    members: false, view3d: false, pdfBranding: false, technicalSheet: false,
    excelExport: false, mapDrawing: false, apiAccess: false,
    maxMembers: 0, maxCustomers: 0, maxEstimates: 0, maxStratigraphies: 0,
  },
};

/**
 * @deprecated Usa `PLAN_RULES`. Mantenuto per retrocompat con componenti che
 * importavano `PLAN_FEATURES` direttamente.
 */
export const PLAN_FEATURES = PLAN_RULES;

/** Hook helper: ritorna se la feature è abilitata nel piano corrente. */
export function useHasFeature(feature: FeatureFlag): boolean {
  const plan = useOrgPlan();
  return PLAN_RULES[plan.code]?.[feature] ?? false;
}

/**
 * Hook helper: ritorna il limite numerico per il piano corrente.
 * Usa `UNLIMITED` (-1) come sentinel per "nessun limite".
 *
 * Esempio:
 *   const maxCustomers = usePlanLimit('maxCustomers');
 *   const isAtLimit = !isUnlimited(maxCustomers) && currentCount >= maxCustomers;
 */
export function usePlanLimit(limit: LimitKey): number {
  const plan = useOrgPlan();
  return PLAN_RULES[plan.code]?.[limit] ?? 0;
}

/** True se il valore è il sentinel "illimitato". */
export function isUnlimited(value: number): boolean {
  return value === UNLIMITED;
}
